import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/styles';
import { TextField, Grid } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import moment from 'moment';
import { produce } from 'immer';
import getConditionTypeHelpers from '../helpers/conditionTypesHelpers';
import { queryParamsValidator as joiValidator } from '../helpers/queryParams';
import { dataTypes, conditionTypeOperators } from '../helpers/constants';
import { convertToDateOnlyString } from 'helpers/timeUtils';
import { Edit } from '@material-ui/icons';
import DateModal from './dateModal';
import { useDispatch, useSelector } from 'react-redux';
import * as schemaActions from 'store/schema/actions';

const useStyles = makeStyles((theme) => ({
  selectContainer: {
    display: 'inline',
    minWidth: '16rem',
    margin: theme.spacing(1)
  },
  error: {
    color: theme.palette.error.main,
    marginTop: theme.spacing(2)
  },
  editIcon: {
    fontSize: '1.15em',
    color: theme.palette.text.secondary,
    cursor: 'pointer'
  },
  autocompleteRoot: {
    marginTop: '-24px'
  }
}));

const Conditions = (props) => {
  const { id, testId, context, template, rule, isReadOnly } = props;
  const { dispatch } = context;
  const reduxDispatch = useDispatch();
  const [value, setValue] = useState(rule.conditionValue || ['']);
  const [errorMessage, setErrorMessage] = useState(rule.errorMessage || ['']);
  const [isDateModalOpen, setIsDateModalOpen] = useState(false);
  const [dateConditions, setDateConditions] = useState({});
  const [currentEditingIndex, setCurrentEditingIndex] = useState(null);
  const selectedDataFrameAttribute = rule.dataFrameAttribute?.attributes?.find(
    (attr) => attr.conditionAttribute === rule.conditionAttribute
  );

  const dropdownOptions = useSelector(
    (state) =>
      state.schema?.schemaOptions?.[selectedDataFrameAttribute?.fieldType] || []
  );

  const validate = (data) => {
    const errors = joiValidator.validate(data, {
      abortEarly: false,
      allowUnknown: true // allow unknown keys that will be ignored
    });
    return errors;
  };

  const { attributes } = template;

  const {
    conditionIsDate,
    conditionIsDateTime,
    conditionIsString,
    conditionIsInteger,
    conditionIsDouble,
    conditionIsBigDecimal,
    conditionIsBoolean,
    conditionIsLong
  } = getConditionTypeHelpers(attributes);

  const classes = useStyles();

  let labels = [];

  useEffect(() => {
    setValue(rule.conditionValue);
    if (rule.dateComparisonOptions) {
      setDateConditions(rule.dateComparisonOptions);
    }
  }, [rule.conditionValue, rule.dateComparisonOptions]);

  let conditionValue = rule.conditionValue;

  if (rule.condition === conditionTypeOperators.RANGE) {
    conditionValue =
      conditionValue.length === 1 ? [conditionValue[0], ''] : conditionValue;

    labels = ['Set Minimum Condition', 'Set Maximum Condition'];
  }

  if (
    rule.condition !== conditionTypeOperators.RANGE &&
    rule.condition !== conditionTypeOperators.IS_EMPTY
  ) {
    conditionValue =
      conditionValue.length > 1 ? [conditionValue[0]] : conditionValue;

    labels = ['Set Condition'];
  }

  let numberOfConditions = conditionValue.map((e, i) => i);

  const parseDoubleOnBlur = (event, index) => {
    let value = event.target.value;

    if (!Number.isNaN(parseFloat(value))) {
      value = parseFloat(value);
    }

    const updatedConditionValue = [...conditionValue];
    updatedConditionValue[index] = value || '';

    onBlur(null, updatedConditionValue);
  };

  const handleChange = (event, index, dataType) => {
    conditionValue = produce(conditionValue, (conditionValueDraft) => {
      let value = event.target.value;

      if (dataType === dataTypes.DATE || dataType === dataTypes.DATE_TIME) {
        if (value instanceof moment) {
          value = convertToDateOnlyString(value);
        } else {
          value = null;
        }
      }

      conditionValueDraft[index] = value || '';
      if (dataType === dataTypes.INTEGER && Number.isInteger(parseInt(value))) {
        conditionValueDraft[index] = parseInt(value);
      }
    });

    onBlur(null, conditionValue);
    setValue(conditionValue);
  };

  const onBlur = (e, aliter, newDateComparisonOptions) => {
    let validation = validate({
      ...rule,
      conditionValue: aliter || value,
      dateComparisonOptions:
        newDateComparisonOptions || rule.dateComparisonOptions
    });
    let errorMessageArray = [];
    validation?.error?.details.forEach((error, index) => {
      if (error.path[1]) {
        errorMessageArray[error.path[1]] = errorMessageArray[error.path[1]]
          ? errorMessageArray[error.path[1]]
          : error.message;
      } else {
        errorMessageArray[index] = error.message;
      }
    });
    setErrorMessage([errorMessageArray[0] || '', errorMessageArray[1] || '']);

    dispatch({
      type: 'setConditionValue',
      id,
      value: aliter || value,
      ...(newDateComparisonOptions && {
        dateComparisonOptions: newDateComparisonOptions
      }),
      errorMessage: [errorMessageArray[0] || '', errorMessageArray[1] || '']
    });
  };

  const isDropdownField =
    selectedDataFrameAttribute &&
    selectedDataFrameAttribute?.fieldType !== 'NONE';

  useEffect(() => {
    if (!isDropdownField || !selectedDataFrameAttribute?.fieldType) {
      return;
    }

    reduxDispatch(
      schemaActions.getSchemaOptions(selectedDataFrameAttribute?.fieldType)
    );
  }, [isDropdownField, selectedDataFrameAttribute?.fieldType, reduxDispatch]);

  if (
    rule.condition === '' ||
    rule.condition === conditionTypeOperators.IS_EMPTY
  ) {
    return null;
  }

  const getDateFormatDisplay = (dateFormatType) => {
    const formatMap = {
      Day: 'DD',
      Month: 'MM',
      Year: 'YYYY',
      DayMonth: 'DD-MM',
      MonthYear: 'MM-YYYY',
      DayMonthYear: 'YYYY-MM-DD'
    };
    return formatMap[dateFormatType] || 'DD';
  };

  const getDateConditionDisplay = (dateOption) => {
    if (!dateOption) return 'Set Condition';

    const {
      dynamicField,
      dateOffset,
      dateFormatType,
      staticDateField,
      dynamicDate
    } = dateOption;

    const formatDisplay = getDateFormatDisplay(dateFormatType);
    const offsetDisplay = `(${dateOffset > 0 ? '+' : ''}${dateOffset})`;

    if (!dynamicDate && staticDateField) {
      return `${staticDateField} ${formatDisplay}`;
    }

    return `${dynamicField}${offsetDisplay} ${formatDisplay}`;
  };

  const handleDateSave = (dateOption) => {
    if (currentEditingIndex !== null) {
      setDateConditions((prev) => ({
        ...prev,
        [currentEditingIndex]: dateOption
      }));

      const newConditionValue = [...(rule.conditionValue || [])];
      const fieldValue = dateOption.staticDateField || dateOption.dynamicField;
      newConditionValue[currentEditingIndex] = fieldValue;

      const newDateComparisonOptions = [...(rule.dateComparisonOptions || [])];
      newDateComparisonOptions[currentEditingIndex] = dateOption;

      setValue(newConditionValue);
      setDateConditions(newDateComparisonOptions);
      onBlur(null, newConditionValue, newDateComparisonOptions);
    }

    setIsDateModalOpen(false);
    setCurrentEditingIndex(null);
  };

  const openDateModal = (index) => {
    setCurrentEditingIndex(index);
    setIsDateModalOpen(true);
  };

  return (
    <Grid item className={classes.selectContainer}>
      {(conditionIsString(rule.conditionDataType) ||
        conditionIsBoolean(rule.conditionDataType)) &&
        numberOfConditions.map((index) => (
          <Grid key={index} item className={classes.selectContainer}>
            {isDropdownField ? (
              <Autocomplete
                classes={{ root: classes.autocompleteRoot }}
                options={dropdownOptions || []}
                getOptionLabel={(option) => option.displayName || ''}
                value={
                  dropdownOptions?.find(
                    (option) => option.id === value[index]
                  ) || null
                }
                onChange={(event, selectedOption) => {
                  handleChange(
                    {
                      target: {
                        value: selectedOption?.id || ''
                      }
                    },
                    index
                  );
                }}
                ListboxProps={{
                  style: {
                    maxHeight: 250,
                    overflowY: 'auto'
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    label={labels[index]}
                    margin="dense"
                    variant="outlined"
                    error={!!errorMessage[index]}
                    helperText={errorMessage[index]}
                  />
                )}
              />
            ) : (
              <TextField
                fullWidth
                label={labels[index]}
                margin="dense"
                disabled={isReadOnly()}
                onChange={(e) =>
                  handleChange(
                    {
                      ...e,
                      target: e.currentTarget
                    },
                    index
                  )
                }
                onBlur={onBlur}
                required
                value={value[index] || ''}
                error={!!errorMessage[index]}
                helperText={errorMessage[index]}
                variant="outlined"
              />
            )}
          </Grid>
        ))}
      {(conditionIsInteger(rule.conditionDataType) ||
        conditionIsLong(rule.conditionDataType)) &&
        numberOfConditions.map((index) => (
          <Grid key={index} item className={classes.selectContainer}>
            <TextField
              fullWidth
              label={labels[index]}
              margin="dense"
              disabled={isReadOnly()}
              onChange={(e) =>
                handleChange(
                  {
                    ...e,
                    target: e.currentTarget
                  },
                  index,
                  dataTypes.INTEGER
                )
              }
              onBlur={onBlur}
              required
              className={classes.select}
              value={value[index]}
              variant="outlined"
              error={errorMessage[index] ? true : false}
              helperText={errorMessage[index]}
              inputProps={{ maxLength: 10 }}
            />
          </Grid>
        ))}
      {(conditionIsDouble(rule.conditionDataType) ||
        conditionIsBigDecimal(rule.conditionDataType)) &&
        numberOfConditions.map((index) => (
          <Grid item key={index} className={classes.selectContainer}>
            <TextField
              fullWidth
              label={labels[index]}
              margin="dense"
              disabled={isReadOnly()}
              onChange={(e) =>
                handleChange(
                  {
                    ...e,
                    target: e.currentTarget
                  },
                  index,
                  rule.conditionDataType
                )
              }
              onBlur={(e) =>
                parseDoubleOnBlur(
                  {
                    ...e,
                    target: e.currentTarget
                  },
                  index
                )
              }
              required
              className={classes.select}
              value={value[index] || ''}
              variant="outlined"
              error={errorMessage[index] ? true : false}
              helperText={errorMessage[index]}
            />
          </Grid>
        ))}
      {(conditionIsDate(rule.conditionDataType) ||
        conditionIsDateTime(rule.conditionDataType)) &&
        numberOfConditions.map((index) => (
          <Grid item key={index}>
            <TextField
              fullWidth
              label={labels[index]}
              margin="dense"
              value={getDateConditionDisplay(dateConditions[index])}
              disabled={isReadOnly()}
              onClick={isReadOnly() ? undefined : () => openDateModal(index)}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <Edit
                    className={classes.editIcon}
                    style={{
                      position: 'static',
                      transform: 'none',
                      opacity: isReadOnly() ? 0.5 : 1,
                      cursor: isReadOnly() ? 'not-allowed' : 'pointer'
                    }}
                  />
                )
              }}
              variant="outlined"
              style={{ cursor: 'pointer' }}
              error={errorMessage[index] ? true : false}
              helperText={errorMessage[index]}
            />
          </Grid>
        ))}
      {isDateModalOpen && (
        <DateModal
          isOpen={isDateModalOpen}
          onClose={() => {
            setIsDateModalOpen(false);
            setCurrentEditingIndex(null);
          }}
          onSave={handleDateSave}
          initialValues={dateConditions[currentEditingIndex]}
        />
      )}
    </Grid>
  );
};

export default Conditions;
